import { BigNumber } from 'bignumber.js';
import { useTranslation } from 'react-i18next';
import { useBalance } from '@/hooks/use-balance';
import {
  CHAIN_ICONS,
  type SupportedTokens,
  TOKEN_ICONS,
} from '@/lib/constants';
import type { CrossChainRoute } from '@/lib/query-options';
import { cn } from '@/lib/utils';
import { formatAddress } from '@/lib/utils/string';
import MingcuteDownLine from '~icons/mingcute/down-line';
import MingcuteWallet from '~icons/mingcute/wallet-line';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { TransferRouteList } from './transfer-route-list';

export type TransferDirection = 'from' | 'to';

interface ChainTokenInputProps {
  dir: TransferDirection;
  route: CrossChainRoute;
  token: SupportedTokens;
  readOnly: boolean;
  amount: string;
  minAmount?: number;
  maxAmount?: number;
  decimals?: number;
  address?: string;
  routes: CrossChainRoute[];
  onChange?: (route: CrossChainRoute) => void;
  onAmountChange?: (amount: string) => void;
}

export const ChainTokenInput = memo(function ChainTokenInput({
  dir,
  route,
  token,
  readOnly,
  minAmount,
  maxAmount,
  amount,
  decimals,
  address,
  routes,
  onChange,
  onAmountChange,
}: ChainTokenInputProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { data: balance, isFetching: isBalanceFetching } = useBalance(
    dir === 'from' ? route.fromChain : route.toChain,
    address,
    dir === 'from',
  );

  const handleSelect = useCallback(
    (selectedRoute: CrossChainRoute, _: any) => {
      onChange?.(selectedRoute);
      setIsOpen(false);
    },
    [onChange],
  );

  const { t } = useTranslation();
  const directionLabel =
    dir === 'from' ? `${t('common.from')}:` : `${t('common.to')}:`;

  const handleAmountBlur = useCallback(() => {
    let value = BigNumber(amount);
    value = value.isNaN() ? BigNumber(0) : value;
    if (value.isNaN()) {
      onAmountChange?.('0.00');
    } else {
      if (minAmount && value.isLessThan(minAmount)) {
        value = BigNumber(minAmount);
      } else if (maxAmount && value.isGreaterThan(maxAmount)) {
        value = BigNumber(maxAmount);
      }

      if (
        typeof decimals === 'number' &&
        (value.decimalPlaces() ?? 0) > decimals
      ) {
        onAmountChange?.(value.toFixed(decimals));
      } else {
        onAmountChange?.(value.toString());
      }
    }
  }, [amount, decimals, onAmountChange, minAmount, maxAmount]);

  const handleAllIn = useCallback(() => {
    if (balance) {
      onAmountChange?.(balance.toString());
    }
  }, [balance, onAmountChange]);

  return (
    <div className="bg-foreground/2 ring-1 ring-foreground/5 backdrop-blur-md rounded-md p-3 space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="font-anta text-sm text-foreground/80">{directionLabel}</h4>

        {dir === 'from' && address && (
          <div className="text-xs flex items-center space-x-2 text-foreground/60">
            <MingcuteWallet className="size-4" />
            {dir === 'from' ? (
              <span>{formatAddress(address)}</span>
            ) : (
              <span className="truncate max-w-[150px]">{formatAddress(address)}</span>
            )}
          </div>
        )}
      </div>

      {dir === 'from' && address && (
        <div className="text-xs -mt-2 text-foreground/60 flex items-center justify-between">
          <span>{t('common.balance')}:</span>

          <div className="flex items-center space-x-1">
            {dir === 'from' && (
              <Button
                variant="link"
                className="font-anta m-0 text-xs p-0 h-auto mr-2"
                onClick={handleAllIn}
              >
                {t('common.allIn')}
              </Button>
            )}

            <span className="font-pt-mono">
              {Intl.NumberFormat('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(balance ?? 0)}
            </span>
            {isBalanceFetching && <Spinner className="size-4" />}
            <img src={TOKEN_ICONS['USDT']} alt={token} className="size-4" />
          </div>
        </div>
      )}

      <div className="flex items-center space-x-2">
        <div className="shrink-0">
          <ChainTokenSelector
            routes={routes}
            dir={dir}
            route={route}
            token={token}
            isOpen={readOnly ? false : isOpen}
            onOpenChange={setIsOpen}
            onSelect={handleSelect}
          />
        </div>

        <Input
          className={cn(
            '!ring-0 !border-0 p-0 shadow-none hide-step !text-2xl !font-bold text-end',
            dir === 'to' && 'text-foreground/60',
          )}
          placeholder="0.00"
          type="number"
          value={amount}
          min={minAmount}
          max={maxAmount}
          onBlur={handleAmountBlur}
          onChange={(e) => {
            onAmountChange?.(e.target.value);
          }}
          readOnly={!onAmountChange || readOnly}
        />
      </div>
    </div>
  );
});

interface ChainTokenSelectorProps {
  routes: CrossChainRoute[];
  route: CrossChainRoute;
  token: SupportedTokens;
  dir: TransferDirection;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (route: CrossChainRoute, token: SupportedTokens) => void;
}

const ChainTokenSelector = memo(function ChainTokenSelector({
  routes,
  route,
  token,
  dir,
  isOpen,
  onOpenChange,
  onSelect,
}: ChainTokenSelectorProps) {
  const { t } = useTranslation();
  if (routes.length) {
    return (
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogTrigger asChild>
          <ChainTokenItem
            route={route}
            dir={dir}
            token={token}
            trailing={<MingcuteDownLine className="text-foreground/80" />}
          />
        </DialogTrigger>

        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-anta">
              {t('transfer.selectTransferRoute')}
            </DialogTitle>
          </DialogHeader>

          <TransferRouteList
            routes={routes}
            dir={dir}
            activeIndex={routes.indexOf(route)}
            onSelect={onSelect}
          />
        </DialogContent>
      </Dialog>
    );
  }

  return <ChainTokenItem route={route} token={token} dir={dir} />;
});

export interface ChainTokenItemProps {
  route: CrossChainRoute;
  token: SupportedTokens;
  dir: TransferDirection;
  onClick?: (route: CrossChainRoute, token: SupportedTokens) => void;
  trailing?: React.ReactNode;
}

export const ChainTokenItem = memo(function ChainTokenItem({
  route,
  token,
  dir,
  onClick,
  trailing,
}: ChainTokenItemProps) {
  const handleClick = useCallback(() => {
    onClick?.(route, token);
  }, [route, token, onClick]);

  const chainId = dir === 'from' ? route.fromChain : route.toChain;

  return (
    <button
      type="button"
      className="w-full bg-background ring-1 ring-inset ring-foreground/10 rounded-md flex items-center p-2 hover:bg-background/80 space-x-2 transition-colors"
      onClick={handleClick}
    >
      <div className="relative ring-1 ring-foreground/10 rounded-full bg-background shadow-sm">
        <img src={TOKEN_ICONS[token]} alt={token} className="size-8 overflow-hidden rounded-full" />
        <img
          src={CHAIN_ICONS[chainId]}
          alt={chainId}
          className="size-3 absolute bottom-0 right-0 overflow-hidden rounded-full bg-background ring-1 ring-foreground/10"
        />
      </div>

      <div className="flex flex-col items-start flex-1 min-w-12">
        <p className="text-sm font-bold leading-tight">{chainId}</p>
        <p className="text-xs text-foreground/50 leading-tight">{token}</p>
      </div>

      {trailing}
    </button>
  );
});


